import { useEffect, useState } from 'react';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Checkbox, FormControlLabel, Link, Stack } from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import './Auth.css';

import { FloatingField } from '../../components';
import AuthLayaout from './AuthLayout';
import { notify } from 'components/Common/Toast';
import { RootState, store } from 'redux/store';
import { login } from 'redux/slices/auth/authSlice';
import MuiLoader from 'components/Common/Loader';
import Button from 'components/Common/Button';
import Routes from 'constants/routes';
import { supportEmail } from 'appConfig';
import { ContactCard } from 'components/Common/ContactCard';
import EmailSharpIcon from '@mui/icons-material/EmailSharp';
import LocalPhoneSharpIcon from '@mui/icons-material/LocalPhoneSharp';
import VerifyPinComponent from 'components/Common/VerifyPinComponent';

const formSchema = yup
    .object({
        username: yup.string().required('Username is required').lowercase().trim(),
        password: yup.string().required('Password is required'),
    })
    .required();

export interface LoginFormInputs {
    username: string;
    password: string;
}

function Login() {
    const authData = useSelector((state: RootState) => state.auth.data);
    const { access_token, need_change_password } = authData || {};
    const isEmailVerify = true;
    // maintained this state for initial loading incase user is authenticated and enter the login route to navigate the user to home
    const [initialLoading, setInitialLoading] = useState<boolean>(false);
    const navigate = useNavigate();
    const dispatch = useDispatch<typeof store.dispatch>();
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [selectMethod, setSelectedMethod] = useState<string>('');
    const [rememberDevice, setRememberDevice] = useState<boolean>(false);

    const {
        control,
        handleSubmit,
        formState: { errors },
    } = useForm<LoginFormInputs>({
        resolver: yupResolver(formSchema),
        defaultValues: {
            username: '',
            password: '',
        },
    });

    useEffect(() => {
        if (need_change_password) {
            return navigate(Routes.account.changePassword);
        }
        if (access_token) {
            navigate(Routes.home.main);
        }
        setInitialLoading(false);
        // eslint-disable-next-line
    }, []);

    const submit = async (data: { [x: string]: any }) => {
        const username = data?.username;
        const password = data?.password;
        setIsLoading(true);
        try {
            let result: any = await dispatch(login({ username, password }));
            if (result?.error || !result?.payload) return;
            const { need_change_password } = result.payload.response || {};
            notify('Logged in successfully!', 'success');
            if (need_change_password) {
                navigate(Routes.account.changePassword);
            } else {
                navigate(Routes.home.main);
            }
        } catch (error: any) {
            notify(error.message, 'error');
            console.log(error);
        } finally {
            setIsLoading(false);
        }
    };

    if (initialLoading) {
        return <MuiLoader absolute loading={initialLoading} />;
    }

    return (
        <AuthLayaout>
            <form onSubmit={handleSubmit(submit)} className="Auth-form">
                <p className="Auth-form-title">Sign in to continue</p>
                {isEmailVerify ? (
                    <>
                        <Controller
                            name="username"
                            control={control}
                            rules={{ required: true }}
                            render={({ field: { onChange, value } }) => (
                                <FloatingField label="Username" value={value} onChange={onChange} error={errors.username?.message} />
                            )}
                        />
                        <Controller
                            name="password"
                            control={control}
                            rules={{ required: true }}
                            render={({ field: { onChange, value } }) => (
                                <FloatingField
                                    label="Password"
                                    value={value}
                                    isPassword={true}
                                    onChange={onChange}
                                    error={errors.password?.message}
                                />
                            )}
                        />
                        <Stack width="100%" alignItems="end">
                            <Button
                                fontSize={14}
                                color="info"
                                variant="text"
                                onClick={() => navigate(Routes.auth.forgotPassword)}
                                height="fit-content"
                                sx={{ '&:hover': { backgroundColor: 'transparent' }, p: '0px' }}
                            >
                                Forgot Password?
                            </Button>
                        </Stack>
                        <FormControlLabel
                            sx={{ my: 1 }}
                            control={
                                <Checkbox
                                    defaultChecked
                                    sx={{
                                        color: '#F3F4FF',
                                        '&.Mui-checked': {
                                            color: '#142456',
                                        },
                                    }}
                                />
                            }
                            label="Remember me and keep me logged in "
                            color="#F3F4FF"
                        />

                        <Button type="submit" disabled={isLoading} loading={isLoading} title="SIGN IN" onClick={handleSubmit(submit)} />

                        <p style={{ alignSelf: 'center' }}>
                            Not a member? Contact us
                            <Link
                                style={{ marginLeft: 4 }}
                                href="#"
                                color="inherit"
                                onClick={(e) => {
                                    window.location.href = `mailto:${supportEmail}`;
                                    e.preventDefault();
                                }}
                            >
                                {supportEmail}
                            </Link>
                        </p>
                    </>
                ) : selectMethod ? (
                    <>
                        <p className="Auth-form-subtitle">Please provide the code we have sent to {selectMethod}</p>
                        <VerifyPinComponent setError={() => {}}>
                            <FormControlLabel
                                style={{ alignSelf: 'center' }}
                                control={
                                    <Checkbox
                                        checked={rememberDevice}
                                        onChange={() => setRememberDevice(!rememberDevice)}
                                        sx={{
                                            borderColor: 'black',
                                            color: '#d3d3d3',
                                            '&.Mui-checked': {
                                                color: '#142456',
                                            },
                                        }}
                                    />
                                }
                                label="Remeber this device"
                            />
                            <Button title="Confirm" onClick={() => {}} />
                        </VerifyPinComponent>
                    </>
                ) : (
                    <>
                        <p className="Auth-form-subtitle">Please choose the verification method you would like to use below.</p>
                        <Stack spacing={2} mt={2}>
                            <ContactCard
                                onClick={() => setSelectedMethod('email')}
                                icon={<EmailSharpIcon sx={{ color: 'black' }} />}
                                text="ABCXXXXX3@xxxx.com"
                            />
                            <ContactCard
                                onClick={() => setSelectedMethod('phone')}
                                icon={<LocalPhoneSharpIcon sx={{ color: 'black' }} />}
                                text="XXX-XXX-XX99"
                            />
                        </Stack>
                    </>
                )}
            </form>
        </AuthLayaout>
    );
}

export default Login;
